import { useCallback, useEffect, useRef } from "react";
import { type AmbienceHeaterType, ViewerAmbience } from "./viewerAmbience";

/**
 * Binds `ViewerAmbience` to viewer state. Thin on purpose: all the audio lives
 * in the plain class, this owns only the React/DOM lifecycle.
 *
 * ## Autoplay policy
 * An `AudioContext` may only be created after a user gesture, so the instance
 * is built lazily. The toggle click itself is that gesture — a capture-phase
 * `click`/`pointerdown`/`keydown` listener on `document` records activation
 * *before* React's own handler runs, so the common path starts instantly. A
 * preference restored from localStorage on a cold load has no gesture yet, so
 * it arms and starts on the user's first interaction with the page instead of
 * creating a context that would only sit suspended.
 */
export interface UseViewerAmbienceOptions {
	enabled: boolean;
	heaterType: AmbienceHeaterType;
	timeOfDay: number;
	/** Called when audio could not start, so the caller can drop the toggle. */
	onUnavailable?: () => void;
}

const GESTURE_EVENTS = [
	"pointerdown",
	"mousedown",
	"keydown",
	"touchend",
	"click",
] as const;

/** Keys the HTML activation-triggering-input spec does not count as a gesture. */
function isNonActivatingKey(event: Event): boolean {
	if (!(event instanceof KeyboardEvent)) return false;
	return (
		event.key === "Escape" || event.metaKey || event.ctrlKey || event.altKey
	);
}

export function useViewerAmbience({
	enabled,
	heaterType,
	timeOfDay,
	onUnavailable,
}: UseViewerAmbienceOptions): void {
	const ambienceRef = useRef<ViewerAmbience | null>(null);
	const hasGestureRef = useRef(false);
	const pendingStartRef = useRef(false);
	const heaterTypeRef = useRef(heaterType);
	const timeOfDayRef = useRef(timeOfDay);
	const onUnavailableRef = useRef(onUnavailable);

	// Declared first so the effects below always read committed values.
	useEffect(() => {
		heaterTypeRef.current = heaterType;
		timeOfDayRef.current = timeOfDay;
		onUnavailableRef.current = onUnavailable;
	}, [heaterType, timeOfDay, onUnavailable]);

	const start = useCallback(() => {
		if (!ambienceRef.current) {
			ambienceRef.current = new ViewerAmbience({
				heaterType: heaterTypeRef.current,
				timeOfDay: timeOfDayRef.current,
			});
		}
		const ambience = ambienceRef.current;
		ambience.setHeaterType(heaterTypeRef.current);
		ambience.setTimeOfDay(timeOfDayRef.current);
		void ambience.start().then((result) => {
			// Only a device that will not play audio may drop the user's stored
			// preference. `"cancelled"` is a straggler whose intent has already
			// been replaced (on -> off -> on inside one resume window), and
			// acting on it would switch off a start that is still coming up.
			if (result !== "unavailable") return;
			if (ambienceRef.current !== ambience) return;
			onUnavailableRef.current?.();
		});
	}, []);

	// Record user activation. Capture phase on `document` so the toggle's own
	// click is seen before React dispatches it, and a restored preference can
	// start on the very same gesture.
	useEffect(() => {
		if (typeof document === "undefined") return;
		const detach = () => {
			for (const type of GESTURE_EVENTS) {
				document.removeEventListener(type, onGesture, true);
			}
		};
		const onGesture = (event: Event) => {
			if (hasGestureRef.current) return;
			// Not every keydown is user activation: per the HTML spec Escape and
			// modifier-only/shortcut chords are excluded, so treating them as a
			// gesture would arm a `resume()` the browser then refuses — and the
			// failed start would wipe the stored preference. Escape is a real
			// first interaction here (the share dialog closes on it).
			if (isNonActivatingKey(event)) return;
			hasGestureRef.current = true;
			detach();
			if (!pendingStartRef.current) return;
			pendingStartRef.current = false;
			start();
		};
		for (const type of GESTURE_EVENTS) {
			document.addEventListener(type, onGesture, true);
		}
		return detach;
	}, [start]);

	useEffect(() => {
		if (enabled) {
			if (hasGestureRef.current) {
				pendingStartRef.current = false;
				start();
			} else {
				// Wait for activation rather than opening a context that would
				// only be born suspended.
				pendingStartRef.current = true;
			}
			return;
		}
		pendingStartRef.current = false;
		ambienceRef.current?.stop();
	}, [enabled, start]);

	useEffect(() => {
		ambienceRef.current?.setHeaterType(heaterType);
	}, [heaterType]);

	useEffect(() => {
		ambienceRef.current?.setTimeOfDay(timeOfDay);
	}, [timeOfDay]);

	// Park the audio thread while the tab is hidden; come back only if the user
	// still has the ambience switched on.
	useEffect(() => {
		if (typeof document === "undefined") return;
		const onVisibilityChange = () => {
			const ambience = ambienceRef.current;
			if (!ambience) return;
			if (document.visibilityState === "hidden") {
				ambience.suspend();
			} else {
				ambience.resume();
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () =>
			document.removeEventListener("visibilitychange", onVisibilityChange);
	}, []);

	useEffect(
		() => () => {
			ambienceRef.current?.dispose();
			ambienceRef.current = null;
		},
		[],
	);
}
